// /app/posts/$postId — single-post view + edit + schedule + cancel + delete.
//
// Layout: two columns on lg+ — post body (left, ~2fr) + sticky action
// panel (right, ~1fr). Stack on mobile. The action panel groups
// status, scheduled time, and all state-transition buttons (schedule,
// publish, cancel, delete) so users have one place to look instead of
// scrolling past a stack of one-button cards.
//
// Schedule, Cancel, and Delete all open Modals rather than expanding
// inline. The card-expansion pattern read as "rough draft" — modals
// read as "operator-grade tool." Server is authoritative on state
// transitions; we show the server's text verbatim on 409/424/etc
// because those errors need to tell the user exactly what to fix.

import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { Textarea } from "../../../components/Textarea";
import { DateTimePicker } from "../../../components/DateTimePicker";
import { Modal } from "../../../components/Modal";
import { StatusPill, postStatusTone } from "../../../components/StatusPill";
import { AssetDownloadButton } from "../../../components/AssetDownloadButton";
import { useI18n } from "../../../lib/i18n";
import { ApiError, assetURL, posts, type Post } from "../../../lib/api";

// Local-time ISO string in the shape the DateTimePicker expects:
// `YYYY-MM-DDTHH:mm`. Does NOT include seconds or timezone marker —
// the parent component converts to UTC ISO at submit time.
function fmtLocalIso(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Default time when the Schedule modal opens: "now + 1 hour" rounded up
// to the next :00 or :30 mark. Gives operators a sensible click-Schedule
// default for "soon, but not in the next 5 minutes."
function defaultScheduleAt(): string {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const m = d.getMinutes();
  if (m < 30) d.setMinutes(30, 0, 0);
  else { d.setMinutes(0, 0, 0); d.setHours(d.getHours() + 1); }
  return fmtLocalIso(d);
}

// Quick-pick presets shown as chips above the date picker. Each returns
// an ISO local-time string. Operator usually wants one of these; the
// popover picker is the escape hatch for "something else."
function quickPickPresets(now: Date = new Date()): { key: string; label: string; iso: string }[] {
  const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
  inOneHour.setSeconds(0, 0);

  const tomorrow9 = new Date(now);
  tomorrow9.setDate(tomorrow9.getDate() + 1);
  tomorrow9.setHours(9, 0, 0, 0);

  const tomorrow18 = new Date(now);
  tomorrow18.setDate(tomorrow18.getDate() + 1);
  tomorrow18.setHours(18, 0, 0, 0);

  // Next Monday at 9am (skip today even if today is Monday — "next" means
  // a future Monday, not this one).
  const nextMon = new Date(now);
  const daysUntilMon = (1 - nextMon.getDay() + 7) % 7 || 7;
  nextMon.setDate(nextMon.getDate() + daysUntilMon);
  nextMon.setHours(9, 0, 0, 0);

  return [
    { key: "1h",      label: "In 1 hour",      iso: fmtLocalIso(inOneHour) },
    { key: "tom9",    label: "Tomorrow 9 AM",  iso: fmtLocalIso(tomorrow9) },
    { key: "tom18",   label: "Tomorrow 6 PM",  iso: fmtLocalIso(tomorrow18) },
    { key: "nextMon", label: "Next Monday",    iso: fmtLocalIso(nextMon) },
  ];
}

function PostDetail() {
  const { t } = useI18n();
  const nav = useNavigate();
  const { postId } = Route.useParams();

  const [p, setP] = useState<Post | null>(null);
  const [loadErr, setLoadErr] = useState<"not_found" | "error" | null>(null);

  const [editing, setEditing] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Edit-mode + schedule-modal local state. Reset from server data on every load.
  const [textBody, setTextBody] = useState("");
  const [hashtagsStr, setHashtagsStr] = useState("");
  const [aiDisclosure, setAiDisclosure] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");

  const [saving, setSaving] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    posts.get(postId)
      .then((row) => {
        if (cancelled) return;
        setP(row);
        hydrate(row);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadErr(e instanceof ApiError && e.status === 404 ? "not_found" : "error");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  function hydrate(row: Post) {
    setTextBody(row.text_body);
    setHashtagsStr(row.hashtags.join(", "));
    setAiDisclosure(row.ai_disclosure);
  }

  function isEditable(row: Post) {
    return row.status === "draft" || row.status === "scheduled";
  }

  async function save() {
    if (!p) return;
    setSaving(true);
    setActionErr(null);
    try {
      const tags = hashtagsStr.split(",").map((s) => s.trim().replace(/^#/, "")).filter(Boolean);
      const updated = await posts.patch(p.id, {
        text_body: textBody.trim(),
        hashtags: tags,
        ai_disclosure: aiDisclosure,
      });
      setP(updated);
      hydrate(updated);
      setEditing(false);
      setSavedToast(true);
      window.setTimeout(() => setSavedToast(false), 2000);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setActionErr(t("post.detail.notEditable"));
      else setActionErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setSaving(false);
    }
  }

  async function doSchedule() {
    if (!p || !scheduleAt) return;
    setSaving(true);
    setActionErr(null);
    try {
      // DateTimePicker gives "YYYY-MM-DDTHH:mm" in local time. Convert to ISO UTC.
      const iso = new Date(scheduleAt).toISOString();
      const updated = await posts.schedule(p.id, iso);
      setP(updated);
      setScheduleOpen(false);
      setSavedToast(true);
      window.setTimeout(() => setSavedToast(false), 2000);
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setSaving(false);
    }
  }

  async function doCancel() {
    if (!p) return;
    setSaving(true);
    setActionErr(null);
    try {
      const updated = await posts.cancel(p.id);
      setP(updated);
      setCancelOpen(false);
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setSaving(false);
    }
  }

  // doPublish — v0.2.3 interim Telegram publish loop.
  // Pre-conditions enforced server-side; the UI surfaces the resulting
  // status message verbatim because the failure modes (channel not
  // configured, bot lacks admin, rate limited) need actionable user text.
  async function doPublish() {
    if (!p) return;
    setSaving(true);
    setActionErr(null);
    try {
      const updated = await posts.publish(p.id);
      setP(updated);
    } catch (e) {
      if (e instanceof ApiError) setActionErr(e.message);
      else setActionErr(t("common.errorGeneric"));
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    if (!p) return;
    setSaving(true);
    setActionErr(null);
    try {
      await posts.delete(p.id);
      void nav({ to: "/app/content/$brandId", params: { brandId: p.brand_id }, replace: true });
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : t("common.errorGeneric"));
    } finally {
      setSaving(false);
    }
  }

  if (loadErr === "not_found") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          <span className="eyebrow">404</span>
          <h2 className="font-display text-xl font-semibold mt-2">{t("notfound.title")}</h2>
          <Link to="/app/content" className="text-secondary hover:text-primary text-sm mt-4 inline-block">
            ← {t("content.title")}
          </Link>
        </Card>
      </div>
    );
  }
  if (loadErr || !p) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Card>
          {loadErr ? <p className="text-error">{t("common.errorGeneric")}</p>
            : <p className="text-secondary">{t("common.loading")}</p>}
        </Card>
      </div>
    );
  }

  const editable = isEditable(p);
  const canPublishTelegram =
    p.platform === "telegram" && (p.status === "draft" || p.status === "scheduled");
  const canCancel = p.status === "draft" || p.status === "scheduled";

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-6">
      {/* Breadcrumb + title row */}
      <div className="flex flex-col gap-2">
        {/* v0.5.20 — smart back. If the operator came from the
            gallery, take them back there; otherwise the content list
            (the historical default). document.referrer is a one-way
            check — it isn't reliable across full-reloads, but for
            in-SPA TanStack navigation it works for the typical "I
            clicked through from the gallery" path. */}
        {typeof document !== "undefined" && document.referrer.includes("/gallery") ? (
          <Link
            to="/app/brands/$brandId/gallery"
            params={{ brandId: p.brand_id }}
            className="text-xs text-muted hover:text-primary ring-nexus rounded inline-block w-fit"
          >
            ← {t("post.detail.backToGallery")}
          </Link>
        ) : (
          <Link
            to="/app/content/$brandId"
            params={{ brandId: p.brand_id }}
            className="text-xs text-muted hover:text-primary ring-nexus rounded inline-block w-fit"
          >
            ← {t("content.title")}
          </Link>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-2xl sm:text-3xl font-semibold">{t("post.detail.title")}</h1>
          <StatusPill tone={postStatusTone(p.status)}>{t(`content.post.status.${p.status}`)}</StatusPill>
          <span className="eyebrow">{p.platform}</span>
          {savedToast ? <span className="text-success text-sm" role="status">✓ {t("brands.detail.saved")}</span> : null}
        </div>
      </div>

      {/* Two-column on lg+: post body (left) + action panel (right). */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
        {/* --- Left column: body editor/viewer --- */}
        <div className="flex flex-col gap-6 min-w-0">
          <Card>
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="font-display text-lg font-semibold">{t("post.detail.body")}</h2>
              {!editing && editable ? (
                <Button variant="secondary" onClick={() => setEditing(true)}>{t("post.detail.edit")}</Button>
              ) : null}
            </div>

            {editing ? (
              <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="flex flex-col gap-4" noValidate>
                <Textarea
                  label={t("post.detail.body")}
                  value={textBody}
                  onChange={(e) => setTextBody(e.target.value)}
                  rows={8}
                />
                <Input
                  label={t("post.detail.hashtags")}
                  value={hashtagsStr}
                  onChange={(e) => setHashtagsStr(e.target.value)}
                  placeholder="istanbul, foodie, opening"
                  hint="comma-separated, no # needed"
                />
                <label className="flex items-center gap-2 cursor-pointer w-fit">
                  <input
                    type="checkbox"
                    checked={aiDisclosure}
                    onChange={(e) => setAiDisclosure(e.target.checked)}
                    className="ring-nexus accent-red w-4 h-4"
                  />
                  <span className="text-sm text-secondary">{t("post.detail.aiDisclosure")}</span>
                </label>
                {actionErr ? <p className="text-error text-sm" role="alert">{actionErr}</p> : null}
                <div className="flex items-center justify-end gap-3">
                  <Button variant="ghost" type="button" onClick={() => { hydrate(p); setEditing(false); setActionErr(null); }} disabled={saving}>
                    {t("post.detail.cancel")}
                  </Button>
                  <Button variant="primary" type="submit" loading={saving}>{t("post.detail.save")}</Button>
                </div>
              </form>
            ) : (
              <>
                <p className="text-primary whitespace-pre-wrap text-sm leading-relaxed">{p.text_body}</p>
                {/* hashtags is text[] NOT NULL DEFAULT '{}' server-side,
                    but JSON sometimes drops empty arrays and the client
                    sees undefined → TypeError on .length. Optional
                    chaining covers both shapes safely. */}
                {(p.hashtags?.length ?? 0) > 0 ? (
                  <p className="text-info text-xs mt-3 font-mono">{p.hashtags!.map((h) => `#${h}`).join(" ")}</p>
                ) : null}
                {p.image_url ? (
                  <div className="mt-4 relative inline-block">
                    {/* v0.5.20 — onError fallback + Download button.
                        If the image fails to load (CDN hiccup, asset
                        moved), show a clear retry button instead of
                        the browser's broken-image icon. */}
                    <img
                      src={assetURL(p.image_url)}
                      alt="post"
                      className="rounded-[12px] border border-border-default max-w-full"
                      onError={(e) => {
                        const img = e.currentTarget;
                        // Mark with a data attr so the sibling fallback
                        // displays. Avoids React state thrashing for a
                        // single image's edge case.
                        img.style.display = "none";
                        const sib = img.nextElementSibling as HTMLElement | null;
                        if (sib) sib.style.display = "flex";
                      }}
                    />
                    <div
                      style={{ display: "none" }}
                      className="rounded-[12px] border border-error/30 bg-error/5 p-6 flex-col items-center justify-center gap-2 text-secondary"
                    >
                      <span className="text-error text-xl" aria-hidden>⚠</span>
                      <span className="text-sm">Image failed to load.</span>
                      <a
                        href={assetURL(p.image_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-info hover:underline ring-nexus"
                      >
                        Open in new tab
                      </a>
                    </div>
                    <AssetDownloadButton
                      imageUrl={p.image_url}
                      triggerClassName="absolute top-2 right-2"
                    />
                  </div>
                ) : null}
                {p.ai_disclosure ? (
                  <p className="text-xs text-info mt-3 font-mono">✓ {t("post.detail.aiDisclosure")}</p>
                ) : null}
              </>
            )}
          </Card>

          {/* Published — show the platform link, or failure reason. */}
          {p.status === "published" && p.platform_permalink ? (
            <Card>
              <h2 className="font-display text-lg font-semibold mb-2">{t("post.detail.publishedTitle")}</h2>
              <a
                href={p.platform_permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-info hover:underline break-all font-mono text-sm"
              >
                {p.platform_permalink} ↗
              </a>
              {p.published_at ? (
                <p className="text-xs text-muted mt-2">
                  {new Date(p.published_at).toLocaleString()}
                </p>
              ) : null}
            </Card>
          ) : p.status === "failed" && p.failure_reason ? (
            <Card>
              <h2 className="font-display text-lg font-semibold mb-2 text-error">{t("post.detail.failedTitle")}</h2>
              <p className="text-sm text-error whitespace-pre-wrap">{p.failure_reason}</p>
            </Card>
          ) : null}
        </div>

        {/* --- Right column: sticky action panel --- */}
        <aside className="lg:sticky lg:top-6 flex flex-col gap-4">
          <Card>
            <h3 className="text-xs uppercase tracking-wider text-muted font-semibold mb-3">
              {t("post.detail.status")}
            </h3>
            <div className="flex items-center gap-2 mb-4">
              <StatusPill tone={postStatusTone(p.status)}>{t(`content.post.status.${p.status}`)}</StatusPill>
            </div>
            {p.scheduled_for ? (
              <div className="mb-4 p-3 rounded-[10px] bg-elevated border border-border-default">
                <div className="text-[11px] uppercase tracking-wider text-muted font-semibold mb-1">
                  {t("post.detail.scheduledFor")}
                </div>
                <div className="text-sm font-mono text-info">
                  {new Date(p.scheduled_for).toLocaleString()}
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-2">
              {p.status === "draft" ? (
                <Button variant="primary" onClick={() => { setScheduleAt(defaultScheduleAt()); setActionErr(null); setScheduleOpen(true); }}>
                  📅 {t("post.detail.schedule")}
                </Button>
              ) : null}
              {p.status === "scheduled" ? (
                <Button variant="secondary" onClick={() => { setScheduleAt(defaultScheduleAt()); setActionErr(null); setScheduleOpen(true); }}>
                  📅 {t("post.detail.reschedule")}
                </Button>
              ) : null}
              {/* Failed posts: let the operator retry by rescheduling.
                  Backend SchedulePost accepts draft|failed → scheduled
                  and clears failure_reason in the same UPDATE so the
                  stale error doesn't linger on the page. */}
              {p.status === "failed" ? (
                <Button variant="primary" onClick={() => { setScheduleAt(defaultScheduleAt()); setActionErr(null); setScheduleOpen(true); }}>
                  🔄 Retry / Reschedule
                </Button>
              ) : null}
              {canPublishTelegram ? (
                <Button variant="primary" onClick={() => void doPublish()} loading={saving}>
                  🚀 {t("post.detail.publishNow")}
                </Button>
              ) : null}
              {canCancel ? (
                <Button variant="ghost" onClick={() => { setActionErr(null); setCancelOpen(true); }}>
                  {t("post.detail.cancelPost")}
                </Button>
              ) : null}
            </div>

            {actionErr && !scheduleOpen && !cancelOpen && !deleteOpen ? (
              <p className="text-error text-xs mt-3" role="alert">{actionErr}</p>
            ) : null}
          </Card>

          {/* Danger zone — visually separated from primary actions */}
          <Card>
            <h3 className="text-xs uppercase tracking-wider text-error/80 font-semibold mb-3">
              {t("post.detail.dangerZone")}
            </h3>
            <Button
              variant="ghost"
              className="!text-error hover:!bg-error/10 w-full justify-start"
              onClick={() => { setActionErr(null); setDeleteOpen(true); }}
            >
              🗑 {t("post.detail.deletePost")}
            </Button>
          </Card>
        </aside>
      </div>

      {/* --- Schedule modal --- */}
      <Modal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        title={p.status === "scheduled" ? t("post.detail.reschedule") : t("post.detail.schedule")}
        description={t("post.detail.scheduleHint")}
        size="md"
        footer={(
          <>
            <Button variant="ghost" type="button" onClick={() => setScheduleOpen(false)} disabled={saving}>
              {t("post.detail.cancel")}
            </Button>
            <Button variant="primary" type="button" onClick={() => void doSchedule()} loading={saving} disabled={!scheduleAt}>
              {t("post.detail.scheduleSubmit")}
            </Button>
          </>
        )}
      >
        <form onSubmit={(e) => { e.preventDefault(); void doSchedule(); }} className="flex flex-col gap-4" noValidate>
          {/* Quick-pick chips — 90% of operators want one of these.
              Selecting one fills the date picker below; user can
              still tweak before clicking Schedule. */}
          <div className="flex flex-col gap-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-secondary">
              Quick pick
            </span>
            <div className="flex flex-wrap gap-2">
              {quickPickPresets().map((p) => {
                const active = scheduleAt === p.iso;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setScheduleAt(p.iso)}
                    aria-pressed={active}
                    className={[
                      "ring-nexus rounded-[10px] px-3 py-1.5 text-xs border transition",
                      active
                        ? "bg-red text-white border-transparent"
                        : "bg-elevated border-border-default text-secondary hover:text-primary",
                    ].join(" ")}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>
          <DateTimePicker
            label={t("post.detail.scheduleAt")}
            value={scheduleAt}
            onChange={setScheduleAt}
          />
          {/* Echo the selected local time so the operator knows exactly
              what's about to be scheduled (the picker shows local 24h
              but a friendly read-back of weekday + locale-formatted
              time prevents off-by-one-day mistakes on edge dates). */}
          {scheduleAt ? (
            <p className="text-xs text-secondary">
              Will publish on <span className="font-mono text-primary">
                {new Date(scheduleAt).toLocaleString(undefined, {
                  weekday: "long", year: "numeric", month: "long", day: "numeric",
                  hour: "2-digit", minute: "2-digit",
                })}
              </span>
            </p>
          ) : null}
          {actionErr ? <p className="text-error text-sm" role="alert">{actionErr}</p> : null}
        </form>
      </Modal>

      {/* --- Cancel-post confirm modal --- */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={t("post.detail.cancelConfirm.title")}
        description={t("post.detail.cancelConfirm.body")}
        size="sm"
        footer={(
          <>
            <Button variant="ghost" type="button" onClick={() => setCancelOpen(false)} disabled={saving}>
              {t("brands.detail.deleteConfirm.cancel")}
            </Button>
            <Button variant="primary" type="button" onClick={() => void doCancel()} loading={saving}>
              {t("post.detail.cancelPost")}
            </Button>
          </>
        )}
      >
        {actionErr ? <p className="text-error text-sm" role="alert">{actionErr}</p> : null}
      </Modal>

      {/* --- Delete confirm modal --- */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title={t("post.detail.deleteConfirm.title")}
        description={t("post.detail.deleteConfirm.body")}
        tone="danger"
        size="sm"
        footer={(
          <>
            <Button variant="ghost" type="button" onClick={() => setDeleteOpen(false)} disabled={saving}>
              {t("brands.detail.deleteConfirm.cancel")}
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={() => void doDelete()}
              loading={saving}
              className="!bg-error hover:!bg-error/80"
            >
              {t("brands.detail.deleteConfirm.confirm")}
            </Button>
          </>
        )}
      >
        {actionErr ? <p className="text-error text-sm" role="alert">{actionErr}</p> : null}
      </Modal>
    </div>
  );
}

export const Route = createFileRoute("/app/posts/$postId")({
  component: PostDetail,
});
